import { useEffect, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  collectOnboardingDataForTransfer,
  transferOnboardingDataToUser,
} from "@/lib/onboarding-transfer-utils";

/**
 * Component that automatically transfers onboarding data when a user signs up or signs in
 * This should be mounted near the root of the application
 */
export function OnboardingDataTransfer() {
  const { user, isSignedIn, isLoaded } = useUser();
  const hasTransferredRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Only run when Clerk is loaded and user is signed in
    if (!isLoaded || !isSignedIn || !user) {
      return;
    }

    // Check if this is a new user session (different user ID)
    const isNewUserSession = lastUserIdRef.current !== user.id;
    lastUserIdRef.current = user.id;

    // Only transfer data once per user session and if it's a new session
    if (hasTransferredRef.current || !isNewUserSession) {
      return;
    }

    const handleDataTransfer = async () => {
      try {
        console.log(
          "🔍 [TRANSFER] Checking for onboarding data to transfer for user:",
          user.id,
        );

        // Collect data that needs to be transferred
        const transferData = collectOnboardingDataForTransfer();

        // Check if there's any meaningful data to transfer
        const hasApiKeys = Object.keys(transferData.apiKeys).length > 0;
        const hasAccountInfo = !!transferData.accountInfo;
        const hasProgress = !!transferData.onboardingProgress;

        if (hasApiKeys || hasAccountInfo || hasProgress) {
          console.log("🔄 [TRANSFER] Found onboarding data to transfer:", {
            apiKeys: Object.keys(transferData.apiKeys),
            hasAccountInfo,
            hasProgress,
          });

          await transferOnboardingDataToUser(user, transferData);

          console.log(
            "✅ [TRANSFER] Successfully transferred onboarding data to user account",
          );
        } else {
          console.log("ℹ️ [TRANSFER] No onboarding data found to transfer");
        }
      } catch (error) {
        console.error(
          "❌ [TRANSFER] Error during onboarding data transfer:",
          error,
        );
        // Don't throw - we don't want to break the app if transfer fails
      } finally {
        // Mark as transferred regardless of success/failure to prevent retries
        hasTransferredRef.current = true;
      }
    };

    handleDataTransfer();
  }, [isLoaded, isSignedIn, user]);

  // Reset transfer flag when user signs out
  useEffect(() => {
    if (!isSignedIn) {
      hasTransferredRef.current = false;
      lastUserIdRef.current = null;
    }
  }, [isSignedIn]);

  // This component doesn't render anything
  return null;
}
